import * as React from 'react';
import withStyles from 'react-jss';
import { createUseStyles } from 'react-jss';
import { connect } from 'react-redux';
import '@ant-design/compatible/assets/index.css';
import { styles } from './ChatComponentStyle';
import {
  Input,
  Checkbox,
  Switch,
  Button,
  Modal,
  Spin,
  message,
  Form,
} from 'src/design-system/components/atoms';
import useGenericState from '@hooks/useGenericState';
import { addTripComment, fetchTripComments } from 'src/api/tripComments';
import {
  DownloadOutlined,
  PaperClipOutlined,
  CloseOutlined,
} from '@ant-design/icons';
import SecurityIcon from '@components/common/icons/SecurityIcon';
import PdfFileIcon from '@components/common/icons/PdfFileIcon';
import DocFileIcon from '@components/common/icons/DocFileIcon';
import ExcelFileIcon from '@components/common/icons/ExcelFileIcon';
import PngFileIcon from '@components/common/icons/PngFileIcon';
import JpgFileIcon from '@components/common/icons/JpgFileIcon';
import EmptyChatIcon from '@components/common/icons/EmptyChatIcon';
import EmptyFileIcon from '@components/common/icons/EmptyFileIcon';
import { renderTime } from '@utils/utils';
import { useTranslation } from 'react-i18next';

import AttachFiles from './AttachFiles';
import * as moment from 'moment';

const { Search, TextArea } = Input;

const fileWrapperStyle = (theme) => ({
  card: {
    backgroundColor: theme.color.semantic.background.neutral.subtle,
    display: 'flex',
    padding: `${theme.spacing[3]} ${theme.spacing[3]}`,
    borderRadius: theme.radius.sm,
    alignItems: 'center',
    marginTop: theme.spacing[3],
    width: '250px',
  },
  smallCard: {
    backgroundColor: theme.color.semantic.background.neutral.subtle,
    display: 'flex',
    alignItems: 'center',
    fontSize: theme.typography.fontSize.xs,
    width: '250px',
    maxHeight: '20px',
    marginRight: theme.spacing[4],
    justifyContent: 'center',
    padding: theme.spacing[1],
  },
  defaultStyle: {
    backgroundColor: theme.color.semantic.background.neutral.secondary,
    display: 'flex',
    height: '50px',
    padding: theme.spacing[4],
    borderBottom: `${theme.border.width.sm} solid ${theme.color.semantic.border.secondary}`,
    alignItems: 'center',
  },
});

const fileStyle = (theme) => ({
  fileWrapper: ({ type }) =>
    fileWrapperStyle(theme)[type] || fileWrapperStyle(theme).defaultStyle,
  fileMetadata: {
    display: 'flex',
    flexDirection: 'column',
    textOverflow: 'elipsis',
  },
  fileName: {
    fontSize: theme.typography.fontSize.sm,
    fontWeight: theme.typography.fontWeight.semibold,
    textOverflow: 'elipsis',
    maxWidth: '80%',
    overflow: 'hidden',
    whiteSpace: 'nowrap',
  },
  fileProperties: {
    fontSize: theme.typography.fontSize.xs,
    fontWeight: theme.typography.fontWeight.regular,
    color: theme.color.semantic.text.tertiary,
  },
  separator: {
    fontSize: theme.typography.fontSize.xxs,
    color: theme.color.semantic.text.tertiary,
    marginLeft: theme.spacing[1],
    marginRight: theme.spacing[1],
  },
  tag: {
    fontSize: theme.typography.fontSize.xxs,
    backgroundColor: theme.color.semantic.border.secondary,
    height: '18px',
    width: '39px',
    borderRadius: theme.radius.xs,
    lineHeight: theme.typography.fontSize.xs,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    fontWeight: theme.typography.fontWeight.semibold,
    marginLeft: theme.spacing[5],
  },
});

const useStyles = createUseStyles(fileStyle);

const fileIconMap = {
  pdf: <PdfFileIcon />,
  xls: <ExcelFileIcon />,
  docx: <DocFileIcon />,
  doc: <DocFileIcon />,
  xlsx: <ExcelFileIcon />,
  png: <PngFileIcon />,
  jpg: <JpgFileIcon />,
  jpeg: <JpgFileIcon />,
  csv: <ExcelFileIcon />,
};

const File = (props: any) => {
  const { file, theme } = props;
  const classes = useStyles(props);
  const { t } = useTranslation();

  return (
    <div className={classes.fileWrapper}>
      <div
        style={
          props.type === 'smallCard'
            ? { marginRight: theme.spacing[3], fontSize: theme.typography.fontSize.md }
            : { marginRight: theme.spacing[3], fontSize: theme.typography.fontSize.lg }
        }
      >
        {fileIconMap[file.type] || fileIconMap['pdf']}
      </div>
      <div className={classes.fileMetadata}>
        <div className={classes.fileName}>{file.name}</div>
        {props.type === 'card' || props.type === 'smallCard' ? (
          <></>
        ) : (
          <div
            style={{ display: 'flex', alignItems: 'center', marginTop: theme.spacing[1] }}
          >
            <div className={classes.fileProperties}>{file.author}</div>
            <div className={classes.separator}>●</div>
            <div className={classes.fileProperties}>{file.time}</div>
          </div>
        )}
      </div>
      <div style={{ flexGrow: 1 }}>
        {!props.type && file.is_private && (
          <div className={classes.tag}>{t('Private')}</div>
        )}
      </div>
      {props.type !== 'smallCard' && (
        <a
          style={{ color: theme.color.semantic.text.primary }}
          href={file.url}
          target="_blank"
          rel="noreferrer"
        >
          <DownloadOutlined
            style={{ marginRight: theme.spacing[0], fontSize: theme.typography.fontSize.lg, cursor: 'pointer' }}
          />
        </a>
      )}
      {props.type === 'smallCard' && (
        <CloseOutlined
          onClick={() => props.handleRemoveFile(file.id)}
          style={{ marginRight: theme.spacing[0], fontSize: theme.typography.fontSize.md, cursor: 'pointer' }}
        />
      )}
    </div>
  );
};

const Chat = (props: any) => {
  const { chat, classes } = props;
  const { t } = useTranslation();

  return (
    <div className={classes.chatWrapper}>
      <div className={classes.chatMetaData}>
        <div className={classes.chatAuthor}>{chat.author}</div>
        <div className={classes.separator}>●</div>
        <div className={classes.chatTime}>{renderTime(chat.created_at)}</div>
        <div style={{ flexGrow: 1 }}></div>
        {chat.is_private && <div className={classes.tag}>{t('Internal Note')}</div>}
      </div>
      <div className={classes.text}>{chat.conversation}</div>
      <div className={classes.chatAttachments}>
        {chat.attachments?.map((file) => (
          <div style={{ marginRight: props.theme.spacing[4] }}>
            <File type={'card'} file={file} classes={props.classes} theme={props.theme} />
          </div>
        ))}
      </div>
    </div>
  );
};

const AddChat = (props: any) => {
  const { classes, setState, state, fetchChats } = props;
  const { t } = useTranslation();

  const postComment = async () => {
    if (!state?.newChat?.conversation) {
      message.error(t('Please enter a message'));
      return;
    }
    setState({ isLoading: true });
    const { newChat } = state;
    const response = await addTripComment({
      trip_id: newChat?.tripId,
      conversation: newChat?.conversation,
      attachments: newChat?.attachments || [],
      is_private: newChat?.isPrivate,
    });
    if (!response.isSuccess) {
      message.error(response.errorMessage);
    } else {
      setState({
        newChat: {
          tripId: state.newChat?.tripId,
          isPrivate: false,
          attachments: [],
        },
      });
    }
    await fetchChats();
    setState({ isLoading: false });
  };
  return (
    <div className={classes.addChat}>
      <div>
        <TextArea
          onChange={(e) => {
            setState({
              newChat: { ...state.newChat, conversation: e.target.value },
            });
          }}
          value={state?.newChat?.conversation}
          placeholder={t('type_here_dot')}
          className={classes.textArea}
        />
      </div>
      <div
        style={{
          marginLeft: props.theme.spacing[2],
          marginRight: props.theme.spacing[2],
          borderBottom: `${props.theme.border.width.sm} solid ${props.theme.color.semantic.border.soft}`,
        }}
      />
      <div className={classes.privateModeWrapper}>
        <div style={{ flexGrow: 1, display: 'flex', alignItems: 'center' }}>
          <div
            style={{ marginRight: props.theme.spacing[3], fontWeight: props.theme.typography.fontWeight.regular, color: props.theme.color.semantic.text.tertiary }}
          >
            {t('Internal Note')}
          </div>
          <Switch
            onChange={(value) => {
              const newChat = { ...(state.newChat || {}), isPrivate: value };
              setState({ newChat });
            }}
            size="small"
          />
          <div
            style={{
              padding: `${props.theme.spacing[0]} ${props.theme.spacing[2]}`,
              maxWidth: '650px',
              overflowX: 'scroll',
              width: 'fit-content',
            }}
          >
            <div
              style={{ width: 'inherit', overflowX: 'scroll', display: 'flex' }}
            >
              {state?.newChat?.attachments?.map((file, index) => (
                <File
                  type="smallCard"
                  file={file}
                  classes={props.classes}
                  theme={props.theme}
                  key={index}
                  handleRemoveFile={props.handleRemoveFile}
                />
              ))}
            </div>
          </div>
        </div>
        <PaperClipOutlined
          onClick={() => {
            if (state.newChat?.attachments?.length < 3) {
              setState({ isUploadModalVisible: true });
            }
          }}
          style={{
            color: props.theme.color.semantic.text.tertiary,
            fontSize: props.theme.typography.fontSize.xl,
            cursor:
              state.newChat?.attachments?.length >= 3
                ? 'not-allowed'
                : 'pointer',
          }}
        />
        <Button
          onClick={postComment}
          type="primary"
          className={classes.commentButton}
        >
          {t('Comment')}
        </Button>
      </div>
    </div>
  );
};

const ChatComponent = (props: any) => {
  const POLLING_INTERVAL = 20000;
  const pollingAgent = React.useRef({} as any);
  const isMounted = React.useRef(false);
  const { t } = useTranslation();

  const { classes, tripId } = props;
  const [form] = Form.useForm();

  const initialState = {
    isLoading: false,
    isUploadModalVisible: false,
    attachFilesLoader: false,
    showPrivateFiles: false,
    newChat: {
      tripId,
      isPrivate: false,
      attachments: [],
    },
    chats: [],
    files: [],
    fileTypeOptions: [],
    filesToUse: [],
    searchText: '',
  };
  const [state, setState] = useGenericState(initialState);

  const handleFileSearch = (value) => {
    setState({ searchText: value });
  };

  const handleRemoveFile = (id) => {
    const attachments = state.newChat?.attachments?.filter(
      (file) => file.id !== id,
    );
    setState({ newChat: { ...state.newChat, attachments } });
  };
  const renderFiles = (files = []) => {
    return (
      <div className={classes.files}>
        {files.length ? (
          files.map((file) => <File file={file} classes={props.classes} theme={props.theme} />)
        ) : (
          <div className={classes.emptyFileWrapper}>
            <div style={{ fontSize: props.theme.typography.fontSize.xxl }}>
              <EmptyFileIcon />
            </div>
            <div className={classes.fileDescription}>
              {t('Files Shared will appear here')}
            </div>
          </div>
        )}
      </div>
    );
  };

  const renderChats = (chats = []) => {
    return (
      <div className={classes.chats}>
        {chats.length ? (
          chats.map((chat) => <Chat chat={chat} classes={props.classes} theme={props.theme} />)
        ) : (
          <div className={classes.emptyChatWrapper}>
            <div style={{ fontSize: props.theme.typography.fontSize.xxl }}>
              <EmptyChatIcon />
            </div>
            <div className={classes.chatDescription}>
              {t('There aren\'t any comments to show')}
            </div>
          </div>
        )}
      </div>
    );
  };

  const fetchChats = async () => {
    if (!document.hidden) {
      const response = await fetchTripComments({ trip_id: tripId });
      if (response.isSuccess && isMounted.current) {
        const files = [];
        for (const chat of response.data || []) {
          if (Array.isArray(chat.attachments) && chat.attachments.length) {
            for (const file of chat.attachments) {
              if (chat.is_private) {
                file.is_private = true;
              }
              file.author = chat.author;
              file.time = moment
                .utc(chat.created_at)
                .local()
                .format('DD-MM-YYYY HH:mm');
              files.push(file);
            }
          }
        }
        setState({
          chats: response.data,
          files,
          fileTypeOptions: response?.response?.metadata?.documentTypes,
        });
      }
    }
    if (isMounted.current) {
      pollingAgent.current = setTimeout(fetchChats, POLLING_INTERVAL);
    }
  };

  const fetchChatsWithLoader = async () => {
    setState({ isLoading: true });
    try {
      await fetchChats();
    } catch (err) {
      message.error(err.message);
    }
    setState({ isLoading: false });
  };

  React.useEffect(() => {
    isMounted.current = true;
    fetchChatsWithLoader();
    return () => {
      isMounted.current = false;
      if (pollingAgent.current) {
        clearTimeout(pollingAgent.current);
      }
    };
  }, []);

  React.useEffect(() => {
    const filterFunction = (file) => {
      if (state.showPrivateFiles && !file.is_private) {
        return false;
      }
      if (
        state.searchText &&
        !file.name?.toLowerCase().includes(state.searchText?.toLowerCase())
      ) {
        return false;
      }
      return true;
    };
    setState({ filesToUse: state.files.filter(filterFunction) });
  }, [state.showPrivateFiles, state.searchText, state.files]);

  const attachFiles = () => {
    const chat = state.newChat;
    const attachments = state.newChat.attachments || [];
    for (const attachment of (form.getFieldsValue() || {}).attachments || []) {
      if (attachment?.file?.url) {
        attachments.push(attachment.file);
      }
    }
    chat.attachments = attachments;
    setState({ newChat: chat, isUploadModalVisible: false });
  };

  const getFileCount = () => {
    if (state.filesToUse?.length) {
      return <>({state.filesToUse.length})</>;
    }
    return null;
  };

  return (
    <div className={classes.wrapper}>
      {state.isLoading ? (
        <div className={classes.spinner}>
          <Spin />
        </div>
      ) : (
        <>
          <div className={classes.chatComponent}>
            {renderChats(state.chats)}
            {state.newChat?.isPrivate ? (
              <div
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  marginTop: props.theme.spacing[2],
                }}
              >
                <div style={{ fontSize: props.theme.typography.fontSize.xl, marginRight: props.theme.spacing[1] }}>
                  <SecurityIcon />
                </div>
                <div
                  style={{
                    color: props.theme.color.semantic.text.secondary,
                    fontWeight: props.theme.typography.fontWeight.semibold,
                  }}
                >
                  {t('Internal Notes Enabled. Files and messages sent will only be visible to you')}
                </div>
              </div>
            ) : null}
            <div className={classes.divider}></div>
            <AddChat
              state={state}
              classes={props.classes}
              theme={props.theme}
              setState={setState}
              fetchChats={fetchChats}
              handleRemoveFile={handleRemoveFile}
            />
          </div>
          <div className={classes.fileComponent}>
            <div className={classes.fileComponentHeader}>
              <div className={classes.fileCountWrapper}>
                {t('Files')} {getFileCount()}
              </div>
              <Checkbox
                checked={state.showPrivateFiles}
                onChange={(e) =>
                  setState({ showPrivateFiles: e.target.checked })
                }
              >
                <div style={{ fontSize: props.theme.typography.fontSize.sm }}>{t('Private')}</div>
              </Checkbox>
              <Search
                allowClear
                placeholder={t('Search')}
                onSearch={handleFileSearch}
                style={{
                  fontSize: props.theme.typography.fontSize.xs,
                  width: 200,
                  marginLeft: props.theme.spacing[5],
                }}
              />
            </div>
            {renderFiles(state.filesToUse)}
          </div>
          {state.isUploadModalVisible ? (
            <Modal
              title={t('Attach File')}
              visible={state.isUploadModalVisible}
              onCancel={() => {
                setState({ isUploadModalVisible: false });
              }}
              centered
              width={600}
              footer={[
                <Button
                  key="back"
                  onClick={() => {
                    setState({ isUploadModalVisible: false });
                  }}
                >
                  {t('Cancel')}
                </Button>,
                <Button
                  key="submit"
                  type="primary"
                  loading={state.attachFilesLoader}
                  onClick={attachFiles}
                >
                  {t('Submit')}
                </Button>,
              ]}
            >
              <Form form={form}>
                <AttachFiles
                  classes={props.classes}
                  fileTypeOptions={state.fileTypeOptions}
                />
              </Form>
            </Modal>
          ) : null}
        </>
      )}
    </div>
  );
};

const mapStateToProps = ({ masterData }) => {
  return {
    viewType: masterData.viewType,
  };
};

const ChatComponentStyled = withStyles(styles, { injectTheme: true })(
  ChatComponent,
);
export default connect(mapStateToProps, null)(ChatComponentStyled);
